/**
 * DeliveryReviewTip - Jamaican-themed post-delivery review & tip prompt
 * Shows after package is delivered on the tracking page
 * "How was di service?" -> 1-5 stars -> "Yuh want bless di driver wid a tip?"
 */
import { useState } from 'react';
import { Star, Heart, DollarSign, CheckCircle, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';

const TIP_AMOUNTS = [100, 200, 500, 1000];

const DeliveryReviewTip = ({ packageId, driverName, trackingCode, onComplete }) => {
  const [step, setStep] = useState('review'); // review | tip | done
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [tipAmount, setTipAmount] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [submittingTip, setSubmittingTip] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);
  const [tipDone, setTipDone] = useState(false);

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toast.error('Select a rating nuh!');
      return;
    }
    setSubmittingReview(true);
    try {
      await api.post('/reviews/', {
        target_type: 'driver',
        target_id: packageId,
        package_id: packageId,
        rating,
        comment: comment || undefined,
      });
      setReviewDone(true);
      toast.success('Respect! Review submitted');
      setStep('tip');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Couldn\'t send di review';
      if (msg.includes('already')) {
        setReviewDone(true);
        setStep('tip');
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleSubmitTip = async () => {
    const amount = tipAmount || Number(customTip);
    if (!amount || amount <= 0) {
      toast.error('Enter a tip amount nuh!');
      return;
    }
    setSubmittingTip(true);
    try {
      const res = await api.post('/tips/intent', {
        package_id: packageId,
        amount,
      });
      if (res.data.success) {
        // Auto-confirm for now (Stripe integration handles real payments)
        await api.post('/tips/confirm', {
          tip_id: res.data.tip_id,
          payment_intent_id: res.data.client_secret?.split('_secret_')[0] || 'manual',
        });
        setTipDone(true);
        setStep('done');
        toast.success(`Big up! J$${amount} tip sent to ${driverName || 'di driver'}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Couldn\'t send di tip';
      if (msg.includes('already')) {
        setTipDone(true);
        setStep('done');
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmittingTip(false);
    }
  };

  const handleSkipTip = () => {
    setStep('done');
    if (onComplete) onComplete();
  };

  if (step === 'done') {
    return (
      <Card className="bg-gradient-to-br from-emerald-900/40 to-amber-900/40 border-emerald-500/30" data-testid="review-tip-done">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-emerald-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Bless Up! Big Respect</h3>
          <p className="text-slate-400">
            {tipDone
              ? `J$${tipAmount || customTip} tip sent to ${driverName || 'di driver'}. Walk good!`
              : 'Thanks fi di feedback. Walk good!'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-amber-500/30 overflow-hidden" data-testid="review-tip-card">
      {/* Jamaican accent bar */}
      <div className="h-1.5 bg-gradient-to-r from-[#00A86B] via-[#FFD300] to-[#000000]" />

      <CardContent className="p-6">
        {step === 'review' && (
          <div className="space-y-5" data-testid="review-section">
            {/* Header */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-1">How was di service?</h3>
              <p className="text-sm text-slate-400">
                Rate yuh delivery experience wid {driverName || 'di driver'}
              </p>
            </div>

            {/* Star Rating */}
            <div className="flex justify-center gap-2" data-testid="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`h-10 w-10 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-slate-600'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Rating Labels */}
            {rating > 0 && (
              <p className="text-center text-amber-400 font-semibold" data-testid="rating-label">
                {['', 'Lame', 'Could be betta', 'Decent vibes', 'Real good!', 'It sell off'][rating]}
              </p>
            )}

            {/* Comment */}
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Drop a comment if yuh feel like it... (optional)"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 resize-none"
              rows={3}
              data-testid="review-comment"
            />

            {/* Submit */}
            <Button
              onClick={handleSubmitReview}
              disabled={rating === 0 || submittingReview}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3"
              data-testid="submit-review-btn"
            >
              {submittingReview ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <>
                  <Star className="h-4 w-4 mr-2" />
                  Send Di Review
                </>
              )}
            </Button>
          </div>
        )}

        {step === 'tip' && (
          <div className="space-y-5" data-testid="tip-section">
            {/* Review Done Badge */}
            {reviewDone && (
              <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm">
                <CheckCircle className="h-4 w-4" />
                <span>Review sent! Respect</span>
              </div>
            )}

            {/* Tip Header */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-1">
                Yuh want bless di driver wid a tip?
              </h3>
              <p className="text-sm text-slate-400">
                Show {driverName || 'dem'} some love fi di hard work
              </p>
            </div>

            {/* Tip Amount Buttons */}
            <div className="grid grid-cols-4 gap-2" data-testid="tip-amounts">
              {TIP_AMOUNTS.map((amount) => (
                <button
                  key={amount}
                  onClick={() => { setTipAmount(amount); setCustomTip(''); }}
                  className={`p-3 rounded-xl border-2 transition-all font-bold text-center ${
                    tipAmount === amount
                      ? 'border-emerald-400 bg-emerald-500/20 text-emerald-400'
                      : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500'
                  }`}
                  data-testid={`tip-${amount}`}
                >
                  <span className="text-xs block text-slate-500">J$</span>
                  <span className="text-lg">{amount}</span>
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">J$</span>
              <input
                type="number"
                value={customTip}
                onChange={(e) => { setCustomTip(e.target.value); setTipAmount(null); }}
                placeholder="Custom amount"
                className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none"
                data-testid="custom-tip-input"
              />
            </div>

            {/* Tip Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSkipTip}
                className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800"
                data-testid="skip-tip-btn"
              >
                Maybe next time
              </Button>
              <Button
                onClick={handleSubmitTip}
                disabled={(!tipAmount && !customTip) || submittingTip}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                data-testid="send-tip-btn"
              >
                {submittingTip ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Blessing...</>
                ) : (
                  <>
                    <Heart className="h-4 w-4 mr-2" />
                    Bless Di Driver
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeliveryReviewTip;
